import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

// Models
import { TitularResiduosDesbasteViewModel } from "src/app/_core/models/centros/cebe-contract-list-base.model";

// Services
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

@Component({
    selector: "app-titular-residuos-desbaste-autocomplete",
    templateUrl: "./titular-residuos-desbaste-autocomplete.component.html",
    styleUrls: ["./titular-residuos-desbaste-autocomplete.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TitularResiduosDesbasteAutocompleteComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class TitularResiduosDesbasteAutocompleteComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	private readonly destroy$ = new Subject<void>();

	// Inputs
	@Input() label = "Titular de Residuos Desbaste";
	@Input() placeholder =
		"Buscar titular de residuos desbaste por código o descripción";
	@Input() required = false;
	@Input() disabled = false;
	@Input() hint =
		"Busque por código o descripción de titular de residuos desbaste";

	// Outputs
	@Output() titularResiduosDesbasteSelected =
		new EventEmitter<TitularResiduosDesbasteViewModel>();

	// State
	titularesResiduosDesbaste: TitularResiduosDesbasteViewModel[] = [];
	titularResiduosDesbasteId: number | null = null;
	selectedTitularResiduosDesbaste: TitularResiduosDesbasteViewModel | null =
		null;

	// ControlValueAccessor
	private onChange = (_value: number | null): void => {
		// This is intentional
	};
	private onTouched = (): void => {
		// This is intentional
	};

	constructor(
		private readonly masterService: MasterService,
		private readonly notificationService: NotificationService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.loadTitularesResiduosDesbaste();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ControlValueAccessor implementation
	writeValue(value: number | null): void {
		this.titularResiduosDesbasteId = value ?? null;
		this.syncSelected();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	/**
	 * Carga la lista de titulares de residuos desbaste
	 */
	private loadTitularesResiduosDesbaste(): void {
		this.masterService
			.getTitularResiduosDesbasteList()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (titularesResiduosDesbaste) => {
					this.titularesResiduosDesbaste = titularesResiduosDesbaste;
					this.syncSelected();
					this.cdr.markForCheck();
				},
				error: () => {
					this.notificationService.showError(
						"Error al cargar los titulares de residuos desbaste",
					);
				},
			});
	}

	/** Recalcula el objeto seleccionado a partir del id actual. */
	private syncSelected(): void {
		this.selectedTitularResiduosDesbaste =
			this.titularResiduosDesbasteId != null
				? (this.titularesResiduosDesbaste.find(
						(t) => t.id === this.titularResiduosDesbasteId,
					) ?? null)
				: null;
	}

	/**
	 * Obtiene el texto mostrado para un titular de residuos desbaste
	 */
	displayTitularResiduosDesbaste = (
		titularResiduosDesbaste: TitularResiduosDesbasteViewModel | string,
	): string => {
		if (typeof titularResiduosDesbaste === "string") {
			return titularResiduosDesbaste;
		}
		return titularResiduosDesbaste &&
			titularResiduosDesbaste.codigo &&
			titularResiduosDesbaste.descripcion
			? `${titularResiduosDesbaste.codigo} - ${titularResiduosDesbaste.descripcion}`
			: "";
	};

	/** Selección desde el autocomplete `.ds`. */
	onValueChange(
		titularResiduosDesbaste: TitularResiduosDesbasteViewModel | null,
	): void {
		this.titularResiduosDesbasteId = titularResiduosDesbaste
			? titularResiduosDesbaste.id
			: null;
		this.selectedTitularResiduosDesbaste = titularResiduosDesbaste;
		this.onChange(this.titularResiduosDesbasteId);
		this.onTouched();
		if (titularResiduosDesbaste) {
			this.titularResiduosDesbasteSelected.emit(titularResiduosDesbaste);
		}
		this.cdr.markForCheck();
	}

	onControlTouched(): void {
		this.onTouched();
	}
}
